import {AxiosInstance, AxiosRequestConfig} from 'axios'
import {acceptedOperation, checkAbort, isOperation, operationPath, pollResearch, ResearchOperationError, waitOperation} from './priceResearchOperations'
import MyAxiosInstancePriceResearch from './axiosInstancePriceResearch'
import {
    AnexoOut,
    AnexosResposta,
    AssistenteOut,
    PriceSearchCreate,
    PriceSearchDetail,
    PriceSearchList,
    PriceSearchOut,
    RevisaoPayload,
} from '../types/priceResearch'

// Service da feature Pesquisa de Preços. Consome o backend price-research em /v1.
// Endpoints (router montado em /v1 com prefixo /pesquisas):
//   POST   /pesquisas            -> 202 PriceSearchOut
//   GET    /pesquisas            -> PriceSearchList
//   GET    /pesquisas/{id}       -> PriceSearchDetail (alvo do polling)
//   DELETE /pesquisas/{id}       -> 204
export class PriceResearchApiService {
    private api: AxiosInstance
    private operations = new AbortController()

    cancelPendingOperations() {
        this.operations.abort()
        this.operations = new AbortController()
    }

    private async pesquisaResponse(data: any, signal: AbortSignal): Promise<PriceSearchOut> {
        if (!isOperation(data)) return data
        await waitOperation(this.api, data, signal)
        checkAbort(signal)
        const id = data.pesquisa_id || data.id
        if (!id) throw new ResearchOperationError('A operação não informou a pesquisa.')
        // Operation success is only a handoff. The research's own status and WS
        // remain authoritative for collection, analysis and final completion.
        return (await this.api.get(`/pesquisas/${id}`, {signal, timeout: 30000})).data
    }

    private async artifact(path: string, config: AxiosRequestConfig = {}): Promise<Blob> {
        const signal = this.operations.signal
        const response = await this.api.get(path, {...config, responseType: 'blob', signal, timeout: 30000})
        const operation = await acceptedOperation(response)
        checkAbort(signal)
        if (!operation) return response.data
        const ready = await waitOperation(this.api, operation, signal)
        if (!ready.artifact_url) throw new ResearchOperationError('A operação terminou sem disponibilizar o arquivo.')
        const file = await this.api.get(operationPath(ready.artifact_url), {responseType: 'blob', signal, timeout: 30000})
        checkAbort(signal)
        if (file.status === 202) throw new ResearchOperationError('O arquivo ainda não está disponível.')
        return file.data
    }

    constructor() {
        this.api = MyAxiosInstancePriceResearch
    }

    // Aceita origem_modulo/origem_ref_id (vínculo de origem no handoff entre
    // módulos, ex.: 'carona' + justificativa_id); o backend persiste e devolve
    // os dois campos no Out/Detail (null em pesquisa avulsa).
    async createPesquisa(payload: PriceSearchCreate): Promise<PriceSearchOut> {
        const signal = this.operations.signal
        const {data} = await this.api.post('/pesquisas', payload, {signal, timeout: 30000})
        return this.pesquisaResponse(data, signal)
    }

    // Saldo de Consultas do usuário (1 Consulta por item). `disponivel: null` = checagem
    // indisponível/desligada — nesse caso o front não deve barrar, só omitir o aviso.
    async getSaldo(): Promise<{disponivel: number | null; max: number | null; current: number | null}> {
        const signal = this.operations.signal
        const response = await this.api.get('/pesquisas/saldo', {signal, timeout: 30000})
        const operation = await acceptedOperation(response)
        checkAbort(signal)
        if (!operation) return response.data
        const ready = await waitOperation(this.api, operation, signal)
        checkAbort(signal)
        const result = (ready as typeof ready & {result?: Record<string, unknown>}).result
        if (!result || !['disponivel', 'max', 'current'].every((key) => Number.isSafeInteger(result[key]))) {
            throw new ResearchOperationError('A operação terminou sem um saldo válido. Tente novamente.')
        }
        return {disponivel: result.disponivel as number, max: result.max as number, current: result.current as number}
    }

    // Assistente "✨ preencher com IA" — campo: objeto | detalhar_item | sugerir_itens |
    // melhorar_descricao | melhorar_itens | fundamentacao_inexigibilidade
    // Endpoint assíncrono: devolve {id, status} de imediato (202); os campos de
    // resultado só vêm preenchidos quando status === 'CONCLUIDO'/'ERRO'. O resultado
    // final chega via notifier (assistenteSocketMessage) ou pelo getAssistente abaixo.
    async assistente(
        campo: string,
        dados: Record<string, any>
    ): Promise<AssistenteOut> {
        const {data} = await this.api.post('/assistente', {campo, dados})
        return data
    }

    // Consulta o status/resultado de um assistente pendente — usado tanto ao reagir
    // ao push do notifier quanto no poll de reconciliação (fallback).
    async getAssistente(id: string): Promise<AssistenteOut> {
        const {data} = await this.api.get(`/assistente/${id}`)
        return data
    }

    // Upload de TR/ETP (PDF/DOCX): a IA extrai os itens e cria a pesquisa.
    async uploadTrEtp(
        file: File,
        periodoMeses: number,
        uf?: string,
        extras?: {processo?: string; orgao?: string}
    ): Promise<PriceSearchOut> {
        const form = new FormData()
        form.append('file', file)
        const signal = this.operations.signal
        const {data} = await this.api.post('/pesquisas/upload', form, {
            signal, timeout: 120000,
            params: {
                periodo_meses: periodoMeses,
                uf: uf || '',
                processo: extras?.processo || '',
                orgao: extras?.orgao || '',
            },
            headers: {'Content-Type': 'multipart/form-data'},
        })
        return this.pesquisaResponse(data, signal)
    }

    // Envia até 3 PDFs como INSUMO DE PREÇO, ANTES de a pesquisa existir. Os ids
    // devolvidos vão em `anexo_ids` no createPesquisa. Não existe endpoint para
    // anexar a uma pesquisa já criada: a coleta dispara junto com o create, e o
    // vínculo tardio perderia a corrida contra o primeiro item coletado.
    async anexarInsumos(files: File[], periodoMeses: number): Promise<AnexosResposta> {
        const form = new FormData()
        files.forEach((f) => form.append('files', f))
        const signal = this.operations.signal
        const {data} = await this.api.post('/pesquisas/anexos', form, {
            signal, timeout: 120000,
            params: {periodo_meses: periodoMeses},
            headers: {'Content-Type': 'multipart/form-data'},
        })
        const anexos: AnexoOut[] = []
        for (const anexo of data.anexos) {
            if (isOperation(anexo)) await waitOperation(this.api, anexo, signal)
            if (isOperation(anexo) || anexo.ia_status === 'PENDENTE') {
                anexos.push(await pollResearch<AnexoOut>(
                    async (timeout) => (await this.api.get(`/pesquisas/anexos/${anexo.id}`, {signal, timeout})).data,
                    (value) => ['CONCLUIDA', 'FALHOU', 'NAO_APLICAVEL'].includes(value.ia_status || ''),
                    signal,
                ))
            } else anexos.push(anexo)
        }
        return {...data, anexos}
    }

    // Estado atual de UM anexo. Chamado quando o notifier avisa que a leitura por IA
    // terminou — push diz "mudou", o front busca. Não é polling.
    async obterAnexo(anexoId: string): Promise<AnexoOut> {
        const {data} = await this.api.get(`/pesquisas/anexos/${anexoId}`)
        return data
    }

    async listPesquisas(
        limit: number = 20,
        offset: number = 0
    ): Promise<PriceSearchList> {
        const {data} = await this.api.get('/pesquisas', {
            params: {limit, offset},
        })
        return data
    }

    async getPesquisa(id: string, signal?: AbortSignal): Promise<PriceSearchDetail> {
        const {data} = await this.api.get(`/pesquisas/${id}`, {signal, timeout: 30000})
        return data
    }

    // Refazer: reprocessa em background uma pesquisa que terminou em ERRO (ou que
    // travou em GERANDO_RELATORIO). Reaproveita os itens já concluídos — só os que
    // faltaram são refeitos. 202: o progresso volta pelo notifier, como na criação.
    // Reenvia à análise os preços "análise não retornou", SEM nova coleta —
    // caminho barato quando o n8n estava lento/indisponível e voltou.

    async retentar(id: string): Promise<PriceSearchOut> {
        const signal = this.operations.signal
        const {data} = await this.api.post(`/pesquisas/${id}/retentar`, undefined, {signal, timeout: 30000})
        return this.pesquisaResponse(data, signal)
    }

    async reanalisar(id: string): Promise<PriceSearchOut> {
        const signal = this.operations.signal
        const {data} = await this.api.post(`/pesquisas/${id}/reanalisar`, undefined, {signal, timeout: 30000})
        return this.pesquisaResponse(data, signal)
    }

    // Revisão da cesta: aplica escolhas do usuário e recalcula; retorna o detalhe atualizado.
    async revisar(id: string, payload: RevisaoPayload): Promise<PriceSearchDetail> {
        const signal = this.operations.signal
        const {data} = await this.api.post(`/pesquisas/${id}/revisar`, payload, {signal, timeout: 30000})
        if (!isOperation(data)) return data
        await waitOperation(this.api, data, signal)
        checkAbort(signal)
        return (await this.api.get(`/pesquisas/${id}`, {signal, timeout: 30000})).data
    }

    async deletePesquisa(id: string): Promise<void> {
        await this.api.delete(`/pesquisas/${id}`)
    }

    async arquivarPesquisa(id: string): Promise<void> {
        try {
            await this.api.post(`/pesquisas/${id}/arquivar`)
        } catch (error) {
            console.error('Erro ao arquivar pesquisa:', error)
            throw error
        }
    }

    async desarquivarPesquisa(id: string): Promise<void> {
        try {
            await this.api.post(`/pesquisas/${id}/desarquivar`)
        } catch (error) {
            console.error('Erro ao desarquivar pesquisa:', error)
            throw error
        }
    }

    async listArquivadas(limit = 15, offset = 0): Promise<PriceSearchList> {
        try {
            const {data} = await this.api.get('/pesquisas/arquivadas', {
                params: {limit, offset},
            })
            return data
        } catch (error) {
            console.error('Erro ao buscar pesquisas arquivadas:', error)
            throw error
        }
    }

    // Abre o relatório HTML (com botão "Salvar em PDF") em nova aba.
    async verRelatorio(id: string): Promise<void> {
        const data = await this.artifact(`/pesquisas/${id}/relatorio.html`)
        const url = window.URL.createObjectURL(
            new Blob([data], {type: 'text/html'})
        )
        window.open(url, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    }

    // Baixa o relatório no formato pedido (pdf | xlsx | docx | json).
    async exportar(id: string, formato: 'pdf' | 'xlsx' | 'docx' | 'json'): Promise<void> {
        const data = await this.artifact(`/pesquisas/${id}/export`, {params: {formato}})
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `pesquisa-precos.${formato}`)
        document.body.appendChild(link)
        link.click()
        link.parentNode?.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    // Abre a Solicitação de Cotação (Parâmetro IV) em PDF, em nova aba.
    async solicitacaoCotacao(id: string): Promise<void> {
        const data = await this.artifact(`/pesquisas/${id}/solicitacao-cotacao`)
        const url = window.URL.createObjectURL(new Blob([data], {type: 'application/pdf'}))
        window.open(url, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    }
}
